// Single source of truth for calendar event type metadata.
// Keys must match the `type` values emitted by processEvents() in calendarUtils.js.
export const CALENDAR_EVENT_TYPES = {
	'contract-expiration': {
		color: '#dc3545', // Rojo - Vencimiento de contrato
		pillLabel: 'Venc. Contrato Alq.',
		gridLabel: 'Vencimiento de Contrato',
	},
	'contract-close': {
		color: '#6c757d', // Gris - Finalización
		pillLabel: 'Finalización',
		gridLabel: 'Finalización de Contrato',
	},
	'tenant-income': {
		color: '#0d6efd', // Azul - Ingresos de inquilinos
		pillLabel: 'Ing. Inquilino',
		gridLabel: 'Ingreso de Inquilinos',
	},
	'owner-income': {
		color: '#198754', // Verde - Ingresos de propietarios
		pillLabel: 'Ing. Propietario',
		gridLabel: 'Ingreso de Propietarios',
	},
	'rental-due-expiration': {
		color: '#fd7e14', // Naranja - Cuota alquiler pendiente
		pillLabel: 'Cuota Alq.',
		gridLabel: 'Cuota de Alquiler',
	},
	'rental-due-overdue': {
		color: '#dc3545', // Rojo - Cuota alquiler vencida
		pillLabel: 'Cuota Venc.',
		gridLabel: 'Cuota de Alquiler Vencida',
	},
	'debt-due-expiration': {
		color: '#6f42c1', // Morado - Otra cuota pendiente (deudas)
		pillLabel: 'Cuota Deuda',
		gridLabel: 'Cuota de Deuda',
	},
	'debt-due-overdue': {
		color: '#dc3545', // Rojo - Otra cuota vencida (deudas)
		pillLabel: 'Cuota Venc.',
		gridLabel: 'Cuota de Deuda Vencida',
	},
	'credit-due-expiration': {
		color: '#20c997', // Verde turquesa - Otra cuota pendiente (pasividades)
		pillLabel: 'Cuota Pasiv.',
		gridLabel: 'Cuota de Pasividad',
	},
	'credit-due-overdue': {
		color: '#dc3545', // Rojo - Otra cuota vencida (pasividades)
		pillLabel: 'Cuota Venc.',
		gridLabel: 'Cuota de Pasividad Vencida',
	},
	'bill-expiration': {
		color: '#ffc107', // Amarillo - Gasto pendiente
		pillLabel: 'Gasto',
		gridLabel: 'Vencimiento de Gasto',
	},
	'bill-overdue': {
		color: '#dc3545', // Rojo - Gasto vencido
		pillLabel: 'Gasto Venc.',
		gridLabel: 'Gasto Vencido',
	},
	'google-event': {
		color: '#4285F4', // Azul de Google
		pillLabel: 'Google',
		gridLabel: 'Evento de Google',
	},
};

const DEFAULT_COLOR = '#6c757d';

export function getEventColor(type) {
	return CALENDAR_EVENT_TYPES[type]?.color || DEFAULT_COLOR;
}

export function getEventPillLabel(type) {
	return CALENDAR_EVENT_TYPES[type]?.pillLabel || type;
}

export function getEventGridLabel(type) {
	return CALENDAR_EVENT_TYPES[type]?.gridLabel || type;
}

// Inicio dashboard redesign: consolidates the 9 domain×status colors above
// into the 4 shared entity-list category tokens (--cat-1..4), so the month
// view's legend/cells/detail panel can show exactly 4 category dots instead
// of 9 status colors. Credits fold into Deudas, tenant-income folds into
// Alquileres (contract-linked income); pending-vs-overdue survives as a
// separate red-ring overlay (see `isOverdueType`) rather than a distinct hue.
export const CATEGORY_META = {
	1: {
		label: 'Deudas',
		className: 'tw:bg-cat-1',
		dotClassName: 'tw:bg-cat-1',
		pillActiveClassName: 'tw:bg-cat-1-soft tw:border-cat-1 tw:text-ink-0',
	},
	2: {
		label: 'Alquileres',
		className: 'tw:bg-cat-2',
		dotClassName: 'tw:bg-cat-2',
		pillActiveClassName: 'tw:bg-cat-2-soft tw:border-cat-2 tw:text-ink-0',
	},
	3: {
		label: 'Ventas',
		className: 'tw:bg-cat-3',
		dotClassName: 'tw:bg-cat-3',
		pillActiveClassName: 'tw:bg-cat-3-soft tw:border-cat-3 tw:text-ink-0',
	},
	4: {
		label: 'Activos fijos',
		className: 'tw:bg-cat-4',
		dotClassName: 'tw:bg-cat-4',
		pillActiveClassName: 'tw:bg-cat-4-soft tw:border-cat-4 tw:text-ink-0',
	},
	google: {
		label: 'Google',
		className: 'tw:bg-[#4285F4]',
		dotClassName: 'tw:bg-[#4285F4]',
		pillActiveClassName: 'tw:bg-[#4285F4]/12 tw:border-[#4285F4] tw:text-ink-0',
	},
};

const TYPE_TO_CATEGORY = {
	'debt-due-expiration': 1,
	'debt-due-overdue': 1,
	'credit-due-expiration': 1,
	'credit-due-overdue': 1,
	'contract-expiration': 2,
	'contract-close': 2,
	'rental-due-expiration': 2,
	'rental-due-overdue': 2,
	'tenant-income': 2,
	'owner-income': 3,
	'bill-expiration': 4,
	'bill-overdue': 4,
	'google-event': 'google',
};

export function getEventCategory(type) {
	return TYPE_TO_CATEGORY[type] ?? null;
}

export function isOverdueType(type) {
	return type.endsWith('-overdue');
}

/**
 * Groups a day's events by category for the month-cell rows and the detail
 * panel's `.cal-cat` cards. Returns categories present that day, in a fixed
 * 1→2→3→4→google order (never re-sorted by count/recency).
 */
export function groupEventsByCategory(events) {
	const buckets = {};
	events.forEach(event => {
		const category = getEventCategory(event.type);
		if (category === null) return;
		if (!buckets[category]) {
			buckets[category] = { category, events: [], count: 0, hasOverdue: false };
		}
		buckets[category].events.push(event);
		buckets[category].count += 1;
		if (isOverdueType(event.type)) {
			buckets[category].hasOverdue = true;
		}
	});
	return [1, 2, 3, 4, 'google']
		.filter(category => buckets[category])
		.map(category => ({ ...buckets[category], ...CATEGORY_META[category] }));
}

// Which app-event types can be edited (date only) from the calendar, and how to
// resolve the id of the underlying record. Contracts and asset sales are
// intentionally excluded — their date edits cascade to child debts/dues
// server-side, which is out of scope for a calendar quick-edit.
export function getAppEventEditTarget(event) {
	switch (event.type) {
	case 'rental-due-expiration':
	case 'rental-due-overdue':
	case 'debt-due-expiration':
	case 'debt-due-overdue':
		return event.metadata?.generatedDebt?._id
			? { kind: 'debtDue', id: event.metadata.generatedDebt._id }
			: null;
	case 'credit-due-expiration':
	case 'credit-due-overdue':
		return event.metadata?.generatedCredit?._id
			? { kind: 'creditDue', id: event.metadata.generatedCredit._id }
			: null;
	case 'bill-expiration':
	case 'bill-overdue':
		return event.metadata?.bill?._id
			? { kind: 'bill', id: event.metadata.bill._id }
			: null;
	default:
		return null;
	}
}
